import { computed, inject, Injectable, signal } from '@angular/core';
import { CustomError } from '@common/custom-error';
import { AuthenticateGQL, CurrentPermissionsGQL, CurrentUserGQL, SaveSelectedTenantGQL } from '@graphql';
import { TenantInfo } from '@models/domain/tenant-info';
import { UserInfo } from '@models/domain/user-info';
import { concatMap, map, Observable, switchMap } from 'rxjs';

import jwtDecode from 'jwt-decode';
import { SessionStorageService } from 'ngx-webstorage';

export interface AuthStoreState {
  token: string | null;
  userInfo: UserInfo | null;
  tenantInfo: TenantInfo | null;
  permissions: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private authenticateGQL = inject(AuthenticateGQL);
  private currentUserGQL = inject(CurrentUserGQL);
  private currentPermissionsGQL = inject(CurrentPermissionsGQL);
  private saveSelectedTenantGQL = inject(SaveSelectedTenantGQL);
  private sessionStorageService = inject(SessionStorageService);

  private _state = signal<AuthStoreState>({
    token: this.sessionStorageService.retrieve('token'),
    userInfo: this.sessionStorageService.retrieve('user.info'),
    tenantInfo: this.sessionStorageService.retrieve('user.tenant'),
    permissions: this.sessionStorageService.retrieve('user.permissions') ?? [],
  });

  readonly token = computed(() => this._state().token);
  readonly userInfo = computed(() => this._state().userInfo);
  readonly tenantInfo = computed(() => this._state().tenantInfo);
  readonly permissions = computed(() => this._state().permissions);

  readonly isAuthenticated = computed(() => {
    const token = this._state().token;
    if (!token) return false;
    const date = this.getTokenExpirationDate(token);
    if (!date) return false;
    return date.valueOf() > new Date().valueOf();
  });

  private getTokenExpirationDate(token: string): Date | null {
    try {
      const decoded: any = jwtDecode(token);
      if (decoded.exp === undefined) return null;
      const date = new Date(0);
      date.setUTCSeconds(decoded.exp);
      return date;
    } catch {
      return null;
    }
  }

  setToken(value: string): void {
    this.sessionStorageService.store('token', value);
    this._state.update((s) => ({ ...s, token: value }));
  }

  setUserInfo(value: UserInfo): void {
    this.sessionStorageService.store('user.info', value);
    this._state.update((s) => ({ ...s, userInfo: value }));
  }

  setTenantInfo(value: TenantInfo | null): void {
    if (value) {
      this.sessionStorageService.store('user.tenant', value);
    } else {
      this.sessionStorageService.clear('user.tenant');
    }
    this._state.update((s) => ({ ...s, tenantInfo: value }));
  }

  setPermissions(value: string[]): void {
    this.sessionStorageService.store('user.permissions', value);
    this._state.update((s) => ({ ...s, permissions: value }));
  }

  clearSession(): void {
    this.sessionStorageService.clear();
    this._state.set({ token: null, userInfo: null, tenantInfo: null, permissions: [] });
  }

  authenticate(
    username: string,
    password: string,
  ): Observable<{ userInfo: UserInfo; tenantInfo: TenantInfo | null; permissions: string[]; action: string | null }> {
    return this.authenticateGQL.mutate({ username, password }).pipe(
      concatMap((response) => {
        const jwt = response.data?.authenticate?.jwt;
        if (jwt) {
          this.setToken(jwt);
          return this.currentUser();
        }
        throw CustomError.newInstance('ERROR.AUTHENTICATE.DEFAULT', '');
      }),
    );
  }

  currentUser(): Observable<{ userInfo: UserInfo; tenantInfo: TenantInfo | null; permissions: string[]; action: string | null }> {
    return this.currentUserGQL.fetch().pipe(
      map((response) => {
        const currentUser = response.data.currentUser;
        const userInfo: UserInfo = {
          key: currentUser.key,
          person: {
            key: currentUser.person.key,
            fullName: currentUser.person.fullName,
            firstName: currentUser.person.firstName,
            firstSurname: currentUser.person.firstSurname,
          },
          tenants: currentUser.tenants.nodes.map((tenant) => ({
            key: tenant.key,
            nit: tenant.nit,
            businessName: tenant.businessName,
          })),
        };
        let tenantInfo: TenantInfo | null = null;

        if (currentUser.tenant) {
          tenantInfo = {
            key: currentUser.tenant.key,
            nit: currentUser.tenant.nit,
            businessName: currentUser.tenant.businessName,
          };
        } else if (currentUser.lastTenantKey && currentUser.tenants?.nodes?.length > 0) {
          const lastTenant = currentUser.tenants.nodes[0];
          if (lastTenant) {
            tenantInfo = {
              key: lastTenant.key,
              nit: lastTenant.nit,
              businessName: lastTenant.businessName,
            };
          }
        }

        this.setUserInfo(userInfo);
        this.setTenantInfo(tenantInfo);

        return { userInfo, tenantInfo, action: currentUser.action };
      }),
      switchMap((user) => this.currentPermissions().pipe(map((permissions) => ({ ...user, permissions })))),
    );
  }

  private currentPermissions(): Observable<string[]> {
    return this.currentPermissionsGQL.fetch().pipe(
      map((response) => {
        const permissions = (response.data.currentPermissions?.nodes ?? [])
          .filter((permission) => permission != null)
          .map((permission) => permission.code);
        this.setPermissions(permissions);
        return permissions;
      }),
    );
  }

  saveSelectedTenant(key: string): Observable<any> {
    return this.saveSelectedTenantGQL.mutate({ tenantKey: key });
  }
}
